import { CssBaseline, LinearProgress, ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { AuthProvider } from './auth/AuthProvider.js';
import { RequireAuth, RequireCapability } from './auth/RequireRole.js';
import { AppShell } from './layout/AppShell.js';
import { AccessDeniedPage } from './pages/AccessDeniedPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { LegacyRedirect } from './pages/LegacyRedirect.js';
import { LoginPage } from './pages/LoginPage.js';
import { StyleguidePage } from './pages/StyleguidePage.js';
import { AmlAlertQueuePage } from './pages/aml/AmlAlertQueuePage.js';
import { Customer360Page } from './pages/customers/Customer360Page.js';
import { CustomerActionsPage } from './pages/customers/CustomerActionsPage.js';
import { CustomerListPage } from './pages/customers/CustomerListPage.js';
import { OnboardingApplicationsPage } from './pages/onboarding/OnboardingApplicationsPage.js';
import { ApplicationReviewPage } from './pages/onboarding/review/ApplicationReviewPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { TransactionsPage } from './pages/payments/TransactionsPage.js';
import { ReconciliationDashboardPage } from './pages/reconciliation/ReconciliationDashboardPage.js';
import { ReportsPage } from './pages/reports/ReportsPage.js';
import { SettingsHubPage } from './pages/settings/SettingsHubPage.js';
import { theme } from './theme.js';

const CommissionsPage = lazy(async () => ({ default: (await import('./pages/commissions/CommissionsPage.js')).CommissionsPage }));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function App(): JSX.Element {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              <Route
                element={
                  <RequireAuth>
                    <AppShell>
                      <Outlet />
                    </AppShell>
                  </RequireAuth>
                }
              >
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/commissions" element={<RequireCapability capability="fees.view"><Suspense fallback={<LinearProgress aria-label="Loading commissions" />}><CommissionsPage /></Suspense></RequireCapability>} />
                <Route
                  path="/transactions"
                  element={
                    <RequireCapability capability="transactions.view">
                      <TransactionsPage />
                    </RequireCapability>
                  }
                />
                <Route path="/settings" element={<RequireCapability capability="settings.view"><SettingsHubPage /></RequireCapability>} />
                {/* Identity provider configuration moved into System settings —
                    one place for every partner. Kept as a redirect so an old
                    bookmark lands on the page that replaced it. */}
                <Route
                  path="/settings/providers"
                  element={<LegacyRedirect to="/settings/connections/providers" />}
                />
                <Route path="/styleguide" element={<StyleguidePage />} />

                <Route
                  path="/customers"
                  element={
                    <RequireCapability capability="customers.view">
                      <CustomerListPage />
                    </RequireCapability>
                  }
                />
                <Route
                  path="/customers/:id"
                  element={
                    <RequireCapability capability="customers.view">
                      <Customer360Page />
                    </RequireCapability>
                  }
                />
                <Route
                  path="/customers/:id/actions"
                  element={
                    <RequireCapability capability="customer.actions">
                      <CustomerActionsPage />
                    </RequireCapability>
                  }
                />

                {/* The KYC Queue was folded into Onboarding (DEV-160): an old
                    link opens Onboarding with the "under review" filter. */}
                <Route
                  path="/kyc-queue"
                  element={<Navigate to="/onboarding-applications?status=UNDER_REVIEW" replace />}
                />
                <Route
                  path="/onboarding-applications"
                  element={
                    <RequireCapability capability="kyc.decide">
                      <OnboardingApplicationsPage />
                    </RequireCapability>
                  }
                />
                <Route
                  path="/onboarding-applications/:id"
                  element={
                    <RequireCapability capability="kyc.decide">
                      <ApplicationReviewPage />
                    </RequireCapability>
                  }
                />
                <Route
                  path="/onboarding-policy"
                  element={
                    <RequireCapability capability="settings.compliance">
                      <LegacyRedirect to="/settings/compliance/onboarding-policy" />
                    </RequireCapability>
                  }
                />
                <Route
                  path="/aml-alerts"
                  element={
                    <RequireCapability capability="aml.view">
                      <AmlAlertQueuePage />
                    </RequireCapability>
                  }
                />

                <Route
                  path="/payments"
                  element={
                    <RequireCapability capability="transactions.view">
                      <LegacyRedirect to="/transactions" />
                    </RequireCapability>
                  }
                />
                <Route
                  path="/withdrawals"
                  element={
                    <RequireCapability capability="transactions.view">
                      <LegacyRedirect to="/transactions" source="WITHDRAWAL" />
                    </RequireCapability>
                  }
                />
                {/* Named by old notifications and Slack messages; never a real page. */}
                <Route
                  path="/payments/inbound"
                  element={
                    <RequireCapability capability="transactions.view">
                      <LegacyRedirect to="/transactions" source="INBOUND" />
                    </RequireCapability>
                  }
                />
                <Route
                  path="/incoming-payments"
                  element={
                    <RequireCapability capability="transactions.view">
                      <LegacyRedirect to="/transactions" source="INBOUND" />
                    </RequireCapability>
                  }
                />
                <Route
                  path="/reconciliation"
                  element={
                    <RequireCapability capability="reconciliation.view">
                      <ReconciliationDashboardPage />
                    </RequireCapability>
                  }
                />
                <Route
                  path="/fees"
                  element={
                    <RequireCapability capability="fees.view">
                      <LegacyRedirect to="/settings/pricing/fees" />
                    </RequireCapability>
                  }
                />
                {/* `/settings/<tab>` is the old one-tab address, redirected by the
                    hub; sections live at `/settings/<group>/<section>` (DEV-132). */}
                <Route
                  path="/settings/:section"
                  element={
                    <RequireCapability capability="settings.view">
                      <SettingsHubPage />
                    </RequireCapability>
                  }
                />
                <Route
                  path="/settings/:group/:section"
                  element={
                    <RequireCapability capability="settings.view">
                      <SettingsHubPage />
                    </RequireCapability>
                  }
                />
                <Route
                  path="/settings/system"
                  element={
                    <RequireCapability capability="settings.admin">
                      <LegacyRedirect to="/settings/connections/providers" />
                    </RequireCapability>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <RequireCapability capability="reports.financial">
                      <ReportsPage />
                    </RequireCapability>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <RequireCapability capability="settings.admin">
                      <LegacyRedirect to="/settings/access/admin-users" />
                    </RequireCapability>
                  }
                />
                <Route path="/access-denied" element={<AccessDeniedPage />} />
              </Route>

              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              {/* Not a redirect. An unmatched address used to become the
                  dashboard silently, which turned every broken link into "the
                  button does not work" with nothing to chase (DEV-22). */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
